import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MoreHorizontal, Plus, Trash2, Search } from "lucide-react";
import { StatusPill } from "@/pages/SalesHub";
import { useNavigate } from "react-router-dom";

const PO_STATUS = {
    draft: "bg-zinc-100 text-zinc-700 border-zinc-200",
    confirmed: "bg-blue-50 text-blue-700 border-blue-200",
    received: "bg-emerald-50 text-emerald-700 border-emerald-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
};
function POStatus({ s }) {
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-widest ${PO_STATUS[s] || PO_STATUS.draft}`}>{s}</span>;
}

function CreatePODialog({ open, onOpenChange, onCreated }) {
    const [suppliers, setSuppliers] = useState([]);
    const [products, setProducts] = useState([]);
    const [supplierId, setSupplierId] = useState("");
    // Stable client-side `_k` keeps React reconciliation aligned with the
    // right line row when the user adds or deletes lines.
    const newRow = () => ({
        _k: (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `r-${Date.now()}-${Math.random()}`,
        product_id: "", quantity: 1, unit_price: 0, tax_rate: 0, discount: 0,
    });
    const [items, setItems] = useState(() => [newRow()]);
    const [notes, setNotes] = useState("");
    const [discount, setDiscount] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            (async () => {
                const [s, p] = await Promise.all([api.get("/suppliers"), api.get("/products")]);
                setSuppliers(s.data.items || []); setProducts(p.data.items || []);
            })();
            setSupplierId(""); setItems([newRow()]); setNotes(""); setDiscount(0); setError("");
        }
    }, [open]);

    const pickProduct = (i, pid) => {
        const p = products.find(x => x.id === pid);
        setItems(arr => arr.map((it, idx) => idx === i ? { ...it, product_id: pid, unit_price: p?.cost_price || 0, tax_rate: p?.tax_rate || 0 } : it));
    };
    const setField = (i, k, v) => setItems(arr => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
    const subtotal = items.reduce((s, it) => s + Math.max(Number(it.quantity) * Number(it.unit_price) - Number(it.discount || 0), 0), 0);
    const tax = items.reduce((s, it) => s + Math.max(Number(it.quantity) * Number(it.unit_price) - Number(it.discount || 0), 0) * Number(it.tax_rate || 0) / 100, 0);
    const total = Math.max(subtotal + tax - Number(discount || 0), 0);

    const submit = async (e) => {
        e.preventDefault(); setSaving(true); setError("");
        try {
            const payload = { supplier_id: supplierId, notes, discount: Number(discount), items: items.filter(i => i.product_id).map(i => ({ product_id: i.product_id, quantity: Number(i.quantity), unit_price: Number(i.unit_price), tax_rate: Number(i.tax_rate), discount: Number(i.discount || 0) })) };
            await api.post("/purchase-orders", payload);
            toast.success("PO created"); onCreated(); onOpenChange(false);
        } catch (err) { setError(formatApiError(err)); } finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl" data-testid="po-create-dialog">
                <DialogHeader><DialogTitle className="font-display">New purchase order</DialogTitle><DialogDescription>Receiving a PO auto-increments inventory.</DialogDescription></DialogHeader>
                <form onSubmit={submit} className="space-y-4 max-h-[70vh] overflow-y-auto">
                    <div className="space-y-1.5">
                        <Label className="text-xs">Supplier</Label>
                        <Select value={supplierId} onValueChange={setSupplierId}>
                            <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                            <SelectContent>{suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="rounded-md border border-zinc-200">
                        <div className="grid grid-cols-[1fr_80px_100px_70px_90px_32px] gap-2 border-b border-zinc-100 px-3 py-2 text-[10px] uppercase tracking-widest text-zinc-500">
                            <div>Product</div><div className="text-right">Qty</div><div className="text-right">Cost</div><div className="text-right">Tax %</div><div className="text-right">Total</div><div/>
                        </div>
                        {items.map((it, i) => {
                            const lineSub = Math.max(Number(it.quantity) * Number(it.unit_price) - Number(it.discount || 0), 0);
                            const lineTotal = lineSub * (1 + Number(it.tax_rate || 0) / 100);
                            return (
                                <div key={it._k} className="grid grid-cols-[1fr_80px_100px_70px_90px_32px] gap-2 border-b border-zinc-100 px-3 py-2 items-center">
                                    <Select value={it.product_id} onValueChange={(v) => pickProduct(i, v)}>
                                        <SelectTrigger className="h-8 text-xs" data-testid={`po-item-product-${i}`}><SelectValue placeholder="Select product" /></SelectTrigger>
                                        <SelectContent>{products.map(p => <SelectItem key={p.id} value={p.id}>{p.sku} · {p.name}</SelectItem>)}</SelectContent>
                                    </Select>
                                    <Input type="number" step="any" value={it.quantity} onChange={(e) => setField(i, "quantity", e.target.value)} className="h-8 text-right text-xs" data-testid={`po-item-qty-${i}`} />
                                    <Input type="number" step="any" value={it.unit_price} onChange={(e) => setField(i, "unit_price", e.target.value)} className="h-8 text-right text-xs" />
                                    <Input type="number" step="any" value={it.tax_rate} onChange={(e) => setField(i, "tax_rate", e.target.value)} className="h-8 text-right text-xs" />
                                    <div className="text-right font-mono text-xs">₹{lineTotal.toFixed(2)}</div>
                                    <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => setItems(a => a.filter((_, idx) => idx !== i))} disabled={items.length === 1}><Trash2 className="h-3 w-3" strokeWidth={1.5} /></Button>
                                </div>
                            );
                        })}
                        <div className="px-3 py-2"><Button type="button" size="sm" variant="outline" onClick={() => setItems(a => [...a, newRow()])} data-testid="po-add-line"><Plus className="mr-1 h-3 w-3" strokeWidth={1.5} /> Add line</Button></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5"><Label className="text-xs">Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
                        <div className="space-y-2 rounded-md border border-zinc-200 bg-zinc-50 p-3 text-sm font-mono">
                            <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span>₹{subtotal.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span>₹{tax.toFixed(2)}</span></div>
                            <div className="flex items-center justify-between"><span className="text-zinc-500">Discount</span><Input type="number" step="any" value={discount} onChange={(e) => setDiscount(e.target.value)} className="h-7 w-24 text-right text-xs" /></div>
                            <div className="flex justify-between border-t border-zinc-200 pt-2 font-semibold"><span>Total</span><span data-testid="po-total">₹{total.toFixed(2)}</span></div>
                        </div>
                    </div>
                    {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>}
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button type="submit" disabled={saving || !supplierId} data-testid="po-submit">{saving ? "Saving…" : "Create draft"}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default function Procurement() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/purchase-orders", { params: q ? { q } : {} }); setItems(data.items || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setLoading(false); }
    }, [q]);
    useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

    const action = async (id, path, msg) => {
        try { const { data } = await api.post(`/purchase-orders/${id}/${path}`); toast.success(msg + (data?.item?.number && path === "receive" ? ` · ${data.item.number}` : "")); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };
    const del = async (id) => { try { await api.delete(`/purchase-orders/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err)); } };

    return (
        <div data-testid="page-procurement">
            <PageHeader title="Procurement" description="Purchase orders and goods receipt notes. Receiving a PO auto-increments Inventory." actions={<Button size="sm" onClick={() => setOpen(true)} data-testid="po-new"><Plus className="mr-1.5 h-3.5 w-3.5" strokeWidth={1.5} /> New PO</Button>} />
            <div className="p-8 space-y-4 max-w-[1400px]">
                <div className="flex items-center gap-2">
                    <div className="relative flex-1 max-w-xs">
                        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" strokeWidth={1.5} />
                        <Input placeholder="Search POs…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 h-9" data-testid="po-search" />
                    </div>
                    <div className="ml-auto text-xs text-zinc-500 font-mono">{items.length} POs</div>
                </div>
                <div className="rounded-md border border-zinc-200 bg-white overflow-hidden">
                    <Table>
                        <TableHeader><TableRow className="bg-zinc-50/60">
                            {["PO", "Supplier", "Items", "Total", "Status", "Created", ""].map(h => <TableHead key={h} className={`text-[10px] uppercase tracking-widest text-zinc-500 ${h === "Total" ? "text-right" : ""}`}>{h}</TableHead>)}
                        </TableRow></TableHeader>
                        <TableBody>
                            {loading ? <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">Loading…</TableCell></TableRow>
                            : items.length === 0 ? <TableRow><TableCell colSpan={7} className="py-12 text-center text-sm text-zinc-500">No POs yet.</TableCell></TableRow>
                            : items.map(o => (
                                <TableRow key={o.id} data-testid={`po-row-${o.id}`}>
                                    <TableCell><span className="font-mono text-xs">{o.number}</span></TableCell>
                                    <TableCell><div className="text-sm font-medium">{o.supplier_name}</div></TableCell>
                                    <TableCell><span className="text-xs text-zinc-600">{o.items.length} line{o.items.length > 1 ? "s" : ""}</span></TableCell>
                                    <TableCell className="text-right font-mono text-xs">₹{Number(o.grand_total).toLocaleString("en-IN")}</TableCell>
                                    <TableCell><POStatus s={o.status} /></TableCell>
                                    <TableCell className="text-xs text-zinc-500 font-mono">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`po-actions-${o.id}`}><MoreHorizontal className="h-4 w-4" strokeWidth={1.5} /></Button></DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                {o.status === "draft" && <DropdownMenuItem onClick={() => action(o.id, "confirm", "Confirmed")}>Confirm</DropdownMenuItem>}
                                                {(o.status === "draft" || o.status === "confirmed") && <DropdownMenuItem onClick={() => action(o.id, "receive", "Received into inventory")} data-testid={`po-receive-${o.id}`}>Quick Receive (unbinned)</DropdownMenuItem>}
                                                {(o.status === "draft" || o.status === "confirmed") && <DropdownMenuItem onClick={() => navigate(`/warehouse-hub?po=${o.id}`)} data-testid={`po-guided-receive-${o.id}`}>Guided Receive (bin-level)</DropdownMenuItem>}
                                                {(o.status === "draft" || o.status === "confirmed") && <DropdownMenuItem onClick={() => action(o.id, "cancel", "Cancelled")}>Cancel</DropdownMenuItem>}
                                                {o.status === "draft" && <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => del(o.id)}>Delete</DropdownMenuItem>}
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </div>
            <CreatePODialog open={open} onOpenChange={setOpen} onCreated={load} />
        </div>
    );
}
